/**
 * app/(marketing)/download.tsx - getabeam.com downloads (web, public).
 *
 * Links to the iOS App Store, Google Play, a direct signed APK, and the web app. Store/APK
 * URLs live in src/marketing/content.ts (marked TODO until the listings are live). The web-app
 * tile routes into the login portal.
 *
 * No raw design values; visuals from src/marketing/* + src/components/ui + tokens.
 */
import { StyleSheet, View, useWindowDimensions, type DimensionValue } from 'react-native';
import { Text, Card, Button } from '../../src/components/ui';
import { BeamMark } from '../../src/components/brand/Logo';
import { MarketingShell, useMarketingNav } from '../../src/marketing/shell';
import { Section, Eyebrow } from '../../src/marketing/sections';
import { DOWNLOADS } from '../../src/marketing/content';
import { openExternalUrl } from '../../src/platform/linking';
import { theme } from '../../src/theme';

type Tile = { key: string; platform: string; detail: string; label: string; href: string; internal?: boolean };

const TILES: Tile[] = [
  { key: 'ios', platform: 'iPhone & iPad', detail: 'iOS 15 or later', label: DOWNLOADS.ios.label, href: DOWNLOADS.ios.href },
  { key: 'android', platform: 'Android', detail: 'Android 9 or later', label: DOWNLOADS.android.label, href: DOWNLOADS.android.href },
  { key: 'web', platform: 'Web app', detail: 'Any modern browser, best for reporting', label: DOWNLOADS.web.label, href: DOWNLOADS.web.href, internal: true },
  { key: 'apk', platform: 'Direct APK', detail: 'Sideload the signed build', label: DOWNLOADS.apk.label, href: DOWNLOADS.apk.href },
];

function DownloadBody() {
  const { navigate } = useMarketingNav();
  const { width } = useWindowDimensions();
  const cols = width >= 900 ? 2 : 1;
  const basis = `${100 / cols}%`;

  function open(t: Tile) {
    if (t.internal) navigate(t.href);
    else void openExternalUrl(t.href);
  }

  return (
    <Section tone="paper">
      <Eyebrow>Get the app</Eyebrow>
      <Text variant="display">Beam, everywhere you work</Text>
      <Text variant="subhead" color="secondary" style={styles.lede}>
        Install on the phones in the field and run reporting from the big screen at the office. Your data stays in sync.
      </Text>
      <View style={styles.grid}>
        {TILES.map((t) => (
          <View key={t.key} style={[styles.cell, { flexBasis: basis as DimensionValue, maxWidth: basis as DimensionValue }]}>
            <Card style={styles.tile}>
              <View style={styles.head}>
                <BeamMark size={22} />
                <Text variant="subhead" style={styles.flex1}>
                  {t.platform}
                </Text>
              </View>
              <Text variant="small" color="secondary" style={styles.detail}>
                {t.detail}
              </Text>
              <Button title={t.label} variant={t.key === 'web' ? 'primary' : 'secondary'} onPress={() => open(t)} />
            </Card>
          </View>
        ))}
      </View>
    </Section>
  );
}

export default function DownloadScreen() {
  return (
    <MarketingShell>
      <DownloadBody />
    </MarketingShell>
  );
}

const styles = StyleSheet.create({
  lede: { marginTop: theme.spacing.sm, maxWidth: 560, marginBottom: theme.spacing.xl },
  grid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -theme.spacing.sm },
  cell: { paddingHorizontal: theme.spacing.sm, paddingVertical: theme.spacing.sm },
  tile: { height: '100%', gap: theme.spacing.sm, minHeight: 200 },
  // Logo sits inline next to the heading (no background), matching the homepage feature cards;
  // the detail copy aligns under the heading text via a left inset equal to the logo width + gap.
  head: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  detail: { flex: 1, paddingLeft: 22 + theme.spacing.sm },
  flex1: { flex: 1 },
});
